/**
 * Hardware Rentals hook using React Relay
 *
 * Fetches rental history for a specific hardware item.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useHardwareRentalsRelayQuery as UseHardwareRentalsRelayQuery,
  RentalSortInput,
} from './__generated__/useHardwareRentalsRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware rentals with pagination
 */
const HardwareRentalsQuery = graphql`
  query useHardwareRentalsRelayQuery(
    $id: ID!
    $limit: Int
    $offset: Int
    $sort: [RentalSortInput]
  ) {
    getHardware(id: $id) {
      id
      rentals(limit: $limit, offset: $offset, sort: $sort) {
        id
        oldId
        startTime
        duration
        distance
        chargeAmount
        completed
        rating
        userId
        systemId
        systemOldId
        organizationId
        startLocationOldId
        endLocationOldId
        startLocation {
          id
          name
        }
        endLocation {
          id
          name
        }
        hardwareOldId
        createdOn
        images
        endImageUrl
        user {
          id
          fullName
          firstName
          lastName
          userMemberships {
            id
            approvedOn
            canceledOn
            membership {
              id
              name
            }
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { RentalSortInput };

/**
 * User membership info attached to a rental's user (used for mechanic rental detection).
 */
export interface RentalUserMembershipGraphQL {
  id: string;
  approvedOn?: string | null;
  canceledOn?: string | null;
  membership?: {
    id: string;
    name?: string | null;
  } | null;
}

/**
 * Rental's user info (used for mechanic rental detection and display).
 */
export interface RentalUserGraphQL {
  id: string;
  fullName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userMemberships?: RentalUserMembershipGraphQL[] | null;
}

/**
 * Rental type from GraphQL
 */
export interface RentalGraphQL {
  id: string;
  oldId?: string | null;
  startTime?: string | null;
  duration?: number | null;
  distance?: number | null;
  chargeAmount?: number | null;
  completed?: boolean | null;
  rating?: number | null;
  userId?: string | null;
  systemId?: string | null;
  systemOldId?: string | null;
  organizationId?: string | null;
  startLocationOldId?: string | null;
  endLocationOldId?: string | null;
  startLocation?: { id: string; name?: string | null } | null;
  endLocation?: { id: string; name?: string | null } | null;
  hardwareOldId?: string | null;
  createdOn?: string | null;
  images?: readonly string[] | null;
  endImageUrl?: string | null;
  user?: RentalUserGraphQL | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware rentals using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareRentalsTab({ hardwareId }: { hardwareId: string }) {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <HardwareRentalsContent hardwareId={hardwareId} />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareRentalsContent({ hardwareId }: { hardwareId: string }) {
 *   const rentals = useHardwareRentalsRelay(hardwareId);
 *   return <RentalsTable data={rentals} />;
 * }
 */
export function useHardwareRentalsRelay(
  hardwareId: string,
  options?: {
    limit?: number;
    offset?: number;
    sort?: RentalSortInput[];
  }
) {
  const data = useLazyLoadQuery<UseHardwareRentalsRelayQuery>(HardwareRentalsQuery, {
    id: hardwareId,
    limit: options?.limit ?? 100,
    offset: options?.offset ?? 0,
    sort: options?.sort ?? [{ field: 'START_TIME', order: 'DESC' }],
  });

  return (data.getHardware?.rentals ?? []) as RentalGraphQL[];
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware rentals
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading, refetch } = useHardwareRentalsRelayQuery(hardwareId);
 *
 * if (isLoading) return <Loading />;
 * return <RentalsTable data={data} />;
 */
export function useHardwareRentalsRelayQuery(
  hardwareId: string | undefined,
  options?: {
    enabled?: boolean;
    limit?: number;
    offset?: number;
    sort?: RentalSortInput[];
  }
) {
  const [data, setData] = useState<RentalGraphQL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false && Boolean(hardwareId);
  const limit = options?.limit ?? 100;
  const offset = options?.offset ?? 0;
  const sort = useMemo(
    () => options?.sort ?? [{ field: 'START_TIME' as const, order: 'DESC' as const }],
    [options?.sort]
  );

  useEffect(() => {
    if (!enabled || !hardwareId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

        const result = await fetchQuery<UseHardwareRentalsRelayQuery>(
          relayEnvironment,
          HardwareRentalsQuery,
          { id: hardwareId, limit, offset, sort }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          setData((result.getHardware?.rentals ?? []) as RentalGraphQL[]);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch rentals'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, hardwareId, limit, offset, sort, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, error, refetch]
  );
}
